import collect from 'collect.js';
import { ORDER_METHODS } from '../../services/exports/Constants';
import HelperMethods from '../../services/exports/HelperMethods';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import { useEffect, useMemo, useState } from 'react';
import { useLocationData } from './provider/LocationDataProvider';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useCompanyBusinessTime from '../../hooks/availability/company-business-time/useCompanyBusinessTime';

export enum ORDER_TIME_OPTIONS {
  asap = 'asap',
  scheduled = 'scheduled',
}

export interface OrderTimeOption {
  id: ORDER_TIME_OPTIONS;
  active: boolean;
  title: string;
  description: string;
}

const useOrderTimeOptions = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:useOrderTimeOptions' });
  const { orderMethod, scheduledFor, company } = useLocationData();
  const { hasAsapOrders, hasScheduledOrders, orderingDisabled } = useOrderMethodsStatus(company);
  const { hasOpeningDays } = useCompanyBusinessTime({
    company,
    orderMethod,
    skipHolidays: true,
  });
  const [selectedTimeOption, setSelectedTimeOption] = useState(() =>
    scheduledFor ? ORDER_TIME_OPTIONS.scheduled : null,
  );

  useEffect(() => {
    if (scheduledFor) {
      setSelectedTimeOption(ORDER_TIME_OPTIONS.scheduled);
    }
  }, [scheduledFor]);

  const timeOptions = useMemo<OrderTimeOption[]>(
    () =>
      collect()
        .when(hasAsapOrders() && hasOpeningDays, (data) =>
          data.push({
            id: ORDER_TIME_OPTIONS.asap,
            active: selectedTimeOption === ORDER_TIME_OPTIONS.asap,
            title: t('time_options.asap.title'),
            description: `${HelperMethods.formatTimeInterval(
              orderMethod === ORDER_METHODS.DELIVERY
                ? company?.average_order_delivery_time
                : company?.average_order_preparation_time,
            )} min`,
          }),
        )
        .when(hasScheduledOrders() && hasOpeningDays, (data) =>
          data.push({
            id: ORDER_TIME_OPTIONS.scheduled,
            active: selectedTimeOption === ORDER_TIME_OPTIONS.scheduled,
            title: company?.scheduled_order_option_title ?? t('time_options.scheduled.title'),
            description: scheduledFor ? moment(scheduledFor).format('ddd HH:mm, MMM DD') : null,
          }),
        )
        .toArray(),
    [company, selectedTimeOption, scheduledFor, orderMethod, hasOpeningDays],
  );

  return {
    timeOptions,
    selectedTimeOption,
    setSelectedTimeOption,
    orderingDisabled: orderingDisabled(),
  };
};

export default useOrderTimeOptions;
